import { createSelector } from 'reselect';
import moment from '~/Providers/Moment';
import { DATE_FORMAT } from '~/Redux/Constants';

const byPriorityAsc = (a, b) => {
  if (a.priority < b.priority) return -1;
  if (a.priority > b.priority) return 1;
  return 0;
};

// The occurrence a row belongs to. A recurring task comes back from the API as one
// row per occurrence in the fetched range, so `id` alone does not identify a row —
// `recurrence_id` (or `start`, on an occurrence that has never been edited) is what
// separates them. Same key used by identifierForTask and every `recurrence_id ||
// start` call site; sliced to the date because a moved exception keeps the original
// occurrence date in recurrence_id while start carries a time.
const occurrenceKey = ({ recurrence_id, start }) => (recurrence_id || start || '').substring(0, 10);

// The series a row belongs to. `ics` is stable across a whole recurrence, `id` is
// not — editing a single occurrence mints an exception row with a brand-new id (see
// childReorderPayload). Temp rows created client-side have no ics yet.
const seriesKey = ({ ics, id }) => ics || id;

// Pick the child rows that belong to ONE parent occurrence.
//
// `children` holds every child row of this parent sitting in the store, which in a
// multi-day view (week, month) means every occurrence of every subtask across the
// whole fetched range. Rendering that list under each parent occurrence repeated a
// recurring parent's subtasks once per day still to come — ARTFLT-1569.
const childrenForOccurrence = (children, parent) => {
  if (!children?.length) return [];

  const parentKey = occurrenceKey(parent);

  const bySeries = new Map();
  for (const child of children) {
    const key = seriesKey(child);
    const bucket = bySeries.get(key);
    if (bucket) {
      bucket.push(child);
    } else {
      bySeries.set(key, [child]);
    }
  }

  const selected = [];
  for (const rows of bySeries.values()) {
    const match = rows.find(row => occurrenceKey(row) === parentKey);
    if (match) {
      selected.push(match);
      continue;
    }
    // Nothing lines up with this parent occurrence. A single unmatched row is a child
    // whose own start has drifted from its parent's — it still belongs to the group,
    // which is the pre-existing "children follow the parent" behavior. Several
    // unmatched rows mean this occurrence has no row of its own, so render none rather
    // than borrowing an arbitrary other day's copy.
    if (rows.length === 1) selected.push(rows[0]);
  }

  return selected.sort(byPriorityAsc);
};

export const formattedTasks = createSelector(
  ({
    Tasks: {
      taskCollection: { tasks },
    },
  }) => tasks,
  tasks =>
    tasks
      // A row with no `start` is dropped outright rather than formatted.
      //
      // Every list in the app renders through this selector and there is no
      // error-boundary fallback above it, so reading `start.substring` off such a row
      // took the entire page down -- a blank screen, recoverable only by refreshing
      // (ARTFLT-1576). Merely guarding the read is not enough: a start-less TOP-LEVEL
      // row is excluded anyway, because `moment(undefined)` is invalid and fails
      // `allForDayAndType`'s `day.isSame` test, but a start-less CHILD is not. Children
      // bypass that filter -- they are pulled from the unfiltered collection and
      // narrowed by `childrenForOccurrence`, whose single-unmatched-row fallback then
      // selects the row deliberately. It rendered under EVERY parent occurrence in the
      // range: the same duplicated-subtask symptom this ticket is about, only silent.
      //
      // Dropping it costs that row and nothing else. `subtasksOf` reads the raw store
      // rather than this selector, so a section move still carries such a child along,
      // and `parentIdsWithChildren` is computed separately -- a parent does not stop
      // reporting its children because one of them is malformed.
      .filter(task => {
        if (task.start) return true;
        // Loud enough to diagnose, since nothing else records it and the row stays
        // broken until a refetch: the family-wide update branch restores `start` from
        // the stored row, which is the row that has none.
        console.error('Tasks: dropping a row with no start', task.id);
        return false;
      })
      .map(task => ({
        ...task,
        startDate: moment(task.start, DATE_FORMAT),
        startString: task.start.substring(0, 10),
      }))
      .sort(byPriorityAsc)
);

// The ids of every task that has at least one child anywhere in the store.
//
// Deliberately not derived from what a list renders. A list only renders children
// when it was given `supportsSubtasks`, so a set built from rendered rows reports
// "no children" for every task in the custom list — which is how a parent could be
// dragged out of it, or deleted from it, with its subtasks silently cascaded away
// (ARTFLT-1572). Whether a task has children is a fact about the data, not about
// the box the user happens to be looking at.
//
// Memoized so the returned Set keeps its identity between renders and callers can
// use it without re-rendering every row on every unrelated store change.
export const parentIdsWithChildren = createSelector(
  ({
    Tasks: {
      taskCollection: { tasks },
    },
  }) => tasks,
  tasks => {
    const ids = new Set();
    for (const { parent_id } of tasks) {
      if (parent_id) ids.add(parent_id);
    }
    return ids;
  }
);

export const allExceptType = (tasks, typeToFilter) =>
  tasks.filter(({ type }) => type !== typeToFilter);

export const allForDayAndType = (state, day, typeToSelect, { withSubtasks = false } = {}) => {
  const all = formattedTasks(state);
  const topLevel = all.filter(
    ({ type, startDate, parent_id }) =>
      typeToSelect === type &&
      day.isSame(startDate, 'day') &&
      // Filter out child tasks (parent_id set) defensively. The backend gates
      // `parent_id` exposure behind the SUBTASKS flag, but during rollout windows
      // a child could still leak through — flat-list callers must not render it.
      !parent_id
  );

  if (!withSubtasks) return topLevel;

  // For each top-level task that matches the day/type filter, append its children
  // (ordered by priority) immediately after it. Children are pulled from the full task
  // collection so they appear even if their own start/type doesn't match the parent's
  // filter, then narrowed to the parent's own occurrence by childrenForOccurrence.
  // TODO(product, ARTFLT-1429): confirm intended behavior when a parent's
  // start/type changes — children currently follow the parent across day and
  // type boundaries. Verify with product before subtasks become visible.
  const childrenByParentId = new Map();
  for (const task of all) {
    if (task.parent_id) {
      const bucket = childrenByParentId.get(task.parent_id);
      if (bucket) {
        bucket.push(task);
      } else {
        childrenByParentId.set(task.parent_id, [task]);
      }
    }
  }

  return topLevel.flatMap(parent => [
    parent,
    ...childrenForOccurrence(childrenByParentId.get(parent.id), parent),
  ]);
};

export const fetchingTasks = createSelector(
  ({
    Tasks: {
      taskCollection: { fetching },
    },
  }) => fetching,
  fetching => fetching
);
